import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, leads, geoFrance } from '@afficheservices/db/schema'
import { eq, sql, desc, ilike, isNull } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { LeadCaptureBlock, CallbackForm } from '~/components/lead-capture-block'
import { getSeoContent } from '~/lib/seo-content.server'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { FAQSchema, BreadcrumbSchema, CityPlaceSchema } from '~/components/structured-data'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { formatCityName } from '~/lib/city-format'
import { SERVICE_LABELS, EMERGENCY_SERVICES, artisanSlug } from '~/lib/programmatic-seo'
import { wikidataUrlForInsee, communeImageForInsee } from '~/lib/commune-wikidata.server'
import {
  Wrench, Zap, Paintbrush, Hammer, Home, Key, Snowflake, Truck,
  SprayCan, TreePine, Scissors, ArrowRight,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  plomberie: Wrench, electricite: Zap, peinture: Paintbrush, renovation: Hammer,
  menuiserie: Hammer, toiture: Home, serrurerie: Key, climatisation: Snowflake,
  nettoyage: SprayCan, jardinage: TreePine, demenagement: Truck, 'carrelage-sol': Scissors,
}

const COLOR_MAP: Record<string, { bg: string; icon: string; accent: string }> = {
  plomberie: { bg: 'from-blue-500/10 to-blue-500/5', icon: 'bg-blue-500/15 text-blue-600', accent: 'text-blue-600' },
  electricite: { bg: 'from-amber-500/10 to-amber-500/5', icon: 'bg-amber-500/15 text-amber-600', accent: 'text-amber-600' },
  peinture: { bg: 'from-purple-500/10 to-purple-500/5', icon: 'bg-purple-500/15 text-purple-600', accent: 'text-purple-600' },
  renovation: { bg: 'from-orange-500/10 to-orange-500/5', icon: 'bg-orange-500/15 text-orange-600', accent: 'text-orange-600' },
  menuiserie: { bg: 'from-yellow-500/10 to-yellow-500/5', icon: 'bg-yellow-500/15 text-yellow-700', accent: 'text-yellow-700' },
  toiture: { bg: 'from-red-500/10 to-red-500/5', icon: 'bg-red-500/15 text-red-600', accent: 'text-red-600' },
  serrurerie: { bg: 'from-slate-500/10 to-slate-500/5', icon: 'bg-slate-500/15 text-slate-600', accent: 'text-slate-600' },
  climatisation: { bg: 'from-cyan-500/10 to-cyan-500/5', icon: 'bg-cyan-500/15 text-cyan-600', accent: 'text-cyan-600' },
  nettoyage: { bg: 'from-emerald-500/10 to-emerald-500/5', icon: 'bg-emerald-500/15 text-emerald-600', accent: 'text-emerald-600' },
  jardinage: { bg: 'from-green-500/10 to-green-500/5', icon: 'bg-green-500/15 text-green-600', accent: 'text-green-600' },
  demenagement: { bg: 'from-indigo-500/10 to-indigo-500/5', icon: 'bg-indigo-500/15 text-indigo-600', accent: 'text-indigo-600' },
  'carrelage-sol': { bg: 'from-teal-500/10 to-teal-500/5', icon: 'bg-teal-500/15 text-teal-600', accent: 'text-teal-600' },
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const city = data?.city ?? ''
  const slug = data?.citySlug ?? ''
  const year = new Date().getFullYear()
  const proCount = data?.totalProsInCity ?? 0
  return [
    // Title leads with city + concrete pro count (when known) + year.
    { title: proCount > 0
      ? `Travaux ${city} ${year} : ${proCount} artisans vérifiés | Devis gratuit 48h`
      : `Travaux ${city} ${year} — Artisans vérifiés | Devis gratuit sous 48h` },
    { name: 'description', content: `Travaux à ${city} ${year} : plomberie, électricité, peinture, rénovation. ${proCount > 0 ? `${proCount} artisans locaux SIRET vérifiés.` : 'Artisans SIRET vérifiés + assurés.'} ✓ 3 devis gratuits sous 48h ✓ Avis clients.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/travaux/${slug}` },
    { property: 'og:title', content: `Travaux à ${city} ${year} — AfficheServices` },
    { property: 'og:description', content: `Comparez les devis de ${proCount > 0 ? proCount + ' artisans' : "professionnels"} vérifiés à ${city}. 100 % gratuit.` },
    // Index only cities with rich AI content (else this duplicates the /ville
    // hub and reads thin → "Crawled - currently not indexed").
    { name: 'robots', content: data?.aiContent ? 'index, follow' : 'noindex, follow' },
  ]
}

export async function loader({ params }: LoaderFunctionArgs) {
  const citySlug = params.city ?? ''
  const cityName = formatCityName(citySlug)

  // Full geo row — feeds CityPlaceSchema for Sites de lieux carousel signal.
  const [geoCity] = await db
    .select({
      name: geoFrance.communeName,
      postalCode: geoFrance.postalCode,
      dept: geoFrance.departmentCode,
      deptName: geoFrance.departmentName,
      region: geoFrance.regionName,
      lat: geoFrance.latitude,
      lon: geoFrance.longitude,
      population: geoFrance.population,
      communeCode: geoFrance.communeCode,
    })
    .from(geoFrance)
    .where(sql`LOWER(REPLACE(${geoFrance.communeName}, ' ', '-')) = ${citySlug} OR LOWER(${geoFrance.communeName}) = ${citySlug.replace(/-/g, ' ')}`)
    .orderBy(sql`(${geoFrance.departmentCode} LIKE '97%' OR ${geoFrance.departmentCode} LIKE '98%') ASC`)
    .limit(1)

  // 410 only if every namesake is DOM-TOM (keeps mainland Saint-Denis etc.).
  await assertCityNotDomTom(citySlug)

  // All top-level categories. NOTE: must use isNull() — `= NULL` is never
  // true in SQL, which silently produced an empty grid before.
  const categories = await db.select({
    id: serviceCategories.id,
    name: serviceCategories.nameFr,
    slug: serviceCategories.slug,
  }).from(serviceCategories).where(isNull(serviceCategories.parentId)).orderBy(serviceCategories.nameFr)

  // Pro count per category — single GROUP BY query instead of N+1.
  // Was: 15 sequential roundtrips per page load (one per top-level category).
  const proCountRows = await db.select({
    categoryId: professionalCategories.categoryId,
    count: sql<number>`count(DISTINCT ${professionalCategories.professionalId})::int`,
  })
    .from(professionalCategories)
    .innerJoin(professionalProfiles, eq(professionalProfiles.id, professionalCategories.professionalId))
    .where(ilike(professionalProfiles.city, `%${cityName}%`))
    .groupBy(professionalCategories.categoryId)

  const countByCategoryId = new Map(proCountRows.map((r) => [r.categoryId, r.count]))
  const proCountMap: Record<string, number> = {}
  for (const cat of categories) {
    proCountMap[cat.slug] = countByCategoryId.get(cat.id) ?? 0
  }

  // Total distinct pros in this city for the hero stats strip
  const [totalProsRow] = await db.select({
    count: sql<number>`count(DISTINCT ${professionalProfiles.id})::int`,
  }).from(professionalProfiles).where(ilike(professionalProfiles.city, `%${cityName}%`))
  const totalProsInCity = totalProsRow?.count ?? 0

  // Recent leads in this city
  const recentLeads = await db.select({
    categoryName: serviceCategories.nameFr,
    createdAt: leads.createdAt,
  }).from(leads)
    .leftJoin(serviceCategories, eq(serviceCategories.id, leads.categoryId))
    .where(ilike(leads.city, `%${cityName}%`))
    .orderBy(desc(leads.createdAt))
    .limit(5)

  // FAQs
  const faqs = [
    { question: `Comment trouver un artisan à ${cityName} ?`, answer: `Sur AfficheServices, décrivez votre projet et recevez jusqu'à 3 devis gratuits d'artisans vérifiés à ${cityName} sous 48h.` },
    { question: `Les devis sont-ils gratuits à ${cityName} ?`, answer: `Oui, tous les devis sont 100% gratuits et sans engagement. Vous comparez les prix et choisissez l'artisan qui vous convient.` },
    { question: `Quels travaux peut-on faire réaliser à ${cityName} ?`, answer: `Plomberie, électricité, peinture, rénovation, menuiserie, toiture, climatisation, jardinage, déménagement, et bien plus encore.` },
    { question: `Comment sont sélectionnés les artisans à ${cityName} ?`, answer: `Chaque artisan est vérifié : SIRET valide, assurance professionnelle, avis clients. Nous ne travaillons qu'avec des professionnels qualifiés.` },
  ]

  // Nearby cities
  const nearbyCities = ['Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Lille', 'Rennes', 'Reims', 'Toulon', 'Grenoble', 'Dijon', 'Angers', 'Le Mans', 'Brest', 'Limoges', 'Tours']
    .filter(c => c.toLowerCase() !== cityName.toLowerCase())
    .slice(0, 8)

  // Load AI content for "renovation" (most generic service for city hub pages)
  const aiContent = await getSeoContent('renovation', cityName)

  // Indexable pros in this city — only those with descriptions ≥200 chars
  // (matches sitemap gate). Surface up to 6 to add reverse-links from city
  // hub to pro pages, helping Google discover them faster.
  const indexablePros = await db.select({
    slug: professionalProfiles.slug,
    companyName: professionalProfiles.companyName,
    city: professionalProfiles.city,
  })
    .from(professionalProfiles)
    .where(sql`${professionalProfiles.city} ILIKE ${'%' + cityName + '%'} AND ${professionalProfiles.description} IS NOT NULL AND length(${professionalProfiles.description}) >= 200`)
    .limit(6)

  return {
    city: cityName, citySlug, categories, proCountMap, totalProsInCity, recentLeads, faqs, nearbyCities, aiContent, indexablePros, geoCity: geoCity ?? null,
    wikidataUrl: wikidataUrlForInsee(geoCity?.communeCode ?? null),
    communeImageUrl: communeImageForInsee(geoCity?.communeCode ?? null),
  }
}

export default function TravauxCityPage() {
  const { city, citySlug, categories, proCountMap, totalProsInCity, recentLeads, faqs, nearbyCities, aiContent, indexablePros, geoCity, wikidataUrl, communeImageUrl } = useLoaderData<typeof loader>()
  const canonicalUrl = `https://www.afficheservices.fr/travaux/${citySlug}`

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Travaux', url: 'https://www.afficheservices.fr/toutes-villes' },
        { name: `Travaux à ${city}`, url: canonicalUrl },
      ]} />
      <FAQSchema items={faqs} />
      {geoCity && (
        <CityPlaceSchema
          city={{
            name: geoCity.name,
            postalCode: geoCity.postalCode,
            dept: geoCity.dept,
            deptName: geoCity.deptName,
            region: geoCity.region,
            lat: geoCity.lat,
            lon: geoCity.lon,
            population: geoCity.population,
          }}
          service={{ name: 'Travaux', slug: 'travaux' }}
          canonicalUrl={canonicalUrl}
          wikidataUrl={wikidataUrl}
          communeImageUrl={communeImageUrl}
        />
      )}

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-blue-700 px-4 pt-12 pb-10 text-white sm:pt-16">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-3xl font-extrabold sm:text-4xl lg:text-5xl">
            Travaux à {city}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-blue-100">
            Comparez les devis d'artisans vérifiés à {city}. Service gratuit, réponse sous 48h.
          </p>
          <Link to={`/devis-gratuit`} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-sm font-bold text-primary shadow-lg hover:bg-blue-50 transition-all">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Trust stats — reduces blank space below hero, gives social proof */}
        <div className="mx-auto mt-10 grid max-w-3xl grid-cols-3 gap-3 rounded-2xl bg-white/10 p-4 backdrop-blur-sm sm:gap-6">
          <div className="text-center">
            <p className="text-2xl font-extrabold sm:text-3xl">{totalProsInCity}+</p>
            <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Artisans à {city}</p>
          </div>
          <div className="border-x border-white/15 text-center">
            <p className="text-2xl font-extrabold sm:text-3xl">48h</p>
            <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Réponse moyenne</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-extrabold sm:text-3xl">100%</p>
            <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Gratuit & vérifiés</p>
          </div>
        </div>
      </section>

      {/* Recent activity ticker — proof the city is active */}
      {recentLeads.length > 0 && (
        <section className="border-b border-slate-100 bg-slate-50 px-4 py-3">
          <div className="mx-auto flex max-w-6xl items-center gap-3 text-xs text-slate-500">
            <span className="flex h-2 w-2 shrink-0 rounded-full bg-emerald-500 animate-pulse" />
            <span className="truncate">
              <strong className="text-slate-700">{recentLeads.length}</strong>{' '}
              demande{recentLeads.length > 1 ? 's' : ''} récente{recentLeads.length > 1 ? 's' : ''} à {city} —{' '}
              {recentLeads.slice(0, 3).map((l) => l.categoryName).filter(Boolean).join(', ')}
            </span>
          </div>
        </section>
      )}

      {/* All services grid */}
      <section className="mx-auto max-w-6xl px-4 py-12">
        <h2 className="text-center text-2xl font-bold text-slate-900">
          Tous les services à {city}
        </h2>
        <p className="mt-2 text-center text-sm text-slate-500">
          Cliquez sur un service pour voir les artisans disponibles
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => {
            const Icon = ICON_MAP[cat.slug] ?? Wrench
            const color = COLOR_MAP[cat.slug] ?? 'bg-slate-50 text-slate-600 border-slate-200'
            const artisanSlug = cat.slug === 'plomberie' ? 'plombier'
              : cat.slug === 'electricite' ? 'electricien'
              : cat.slug === 'peinture' ? 'peintre'
              : cat.slug === 'renovation' ? 'renovation'
              : cat.slug === 'menuiserie' ? 'menuisier'
              : cat.slug === 'toiture' ? 'couvreur'
              : cat.slug === 'serrurerie' ? 'serrurier'
              : cat.slug === 'climatisation' ? 'climaticien'
              : cat.slug === 'nettoyage' ? 'nettoyage'
              : cat.slug === 'jardinage' ? 'jardinier'
              : cat.slug === 'demenagement' ? 'demenageur'
              : cat.slug
            const cityUrlSlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')

            return (
              <Link
                key={cat.id}
                to={`/${artisanSlug}-${cityUrlSlug}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300"
              >
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${color.bg} opacity-0 transition-opacity group-hover:opacity-100`} />

                <div className="relative flex items-start gap-4">
                  {/* Icon */}
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${color.icon} transition-transform group-hover:scale-110`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[15px] font-bold text-slate-900">{cat.name}</h3>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {proCountMap[cat.slug] > 0
                        ? `${proCountMap[cat.slug]} artisan${proCountMap[cat.slug] > 1 ? 's' : ''} vérifié${proCountMap[cat.slug] > 1 ? 's' : ''}`
                        : 'Artisans vérifiés'}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 transition-all group-hover:bg-primary group-hover:text-white">
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400 transition-colors group-hover:text-white" />
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Quick links to specific pages */}
      <section className="border-t border-slate-100 bg-slate-50 px-4 py-12">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-xl font-bold text-slate-900">
            Tous les services à {city}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-500">
            20 catégories de travaux couvertes par des artisans vérifiés
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(SERVICE_LABELS)
              .filter(([slug]) => categories.some((c) => c.slug === slug))
              .map(([slug, svc]) => {
                const artisan = artisanSlug(slug)
                const isEmergency = EMERGENCY_SERVICES.includes(slug)
                return (
                  <div key={slug} className="rounded-xl border border-slate-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-slate-900">{svc.name} à {city}</h3>
                    <div className="mt-2 space-y-1">
                      <Link to={`/prix/${artisan}-${citySlug}`} className="block text-xs text-primary hover:underline">
                        Prix {svc.artisan} →
                      </Link>
                      {isEmergency && (
                        <Link to={`/urgence/${artisan}-${citySlug}`} className="block text-xs text-red-600 hover:underline">
                          Urgence 24/7 →
                        </Link>
                      )}
                      <Link to={`/services/${slug}`} className="block text-xs text-slate-500 hover:underline">
                        En savoir plus →
                      </Link>
                    </div>
                  </div>
                )
              })}
          </div>
          {indexablePros.length > 0 && (
            <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Artisans vérifiés à {city}</h3>
              <p className="mt-1 text-xs text-slate-500">Profils complets avec descriptifs détaillés</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {indexablePros.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/pro/${p.slug}`}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary"
                  >
                    {p.companyName}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AI expert content */}
      {aiContent && (
        <section className="mx-auto max-w-3xl px-4 py-8">
          <article className="rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName={city} serviceName="Rénovation" />
            <h2 className="mt-3 text-lg font-bold text-slate-900">Travaux et rénovation à {city} — guide expert</h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug="renovation" />
          </article>
          {aiContent.localContextParas.length > 0 && (
            <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="text-base font-bold text-blue-900">Spécificités locales — {city}</h3>
              <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
                {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          )}
          {aiContent.priceAnalysis && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
              <h3 className="text-base font-bold text-emerald-900">Budget travaux — {city}</h3>
              <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
            </div>
          )}
          <InternaunteQuestions serviceSlug="renovation" cityName={city} artisan="artisan" />
        </section>
      )}

      {/* Lead capture */}
      <section className="mx-auto max-w-2xl px-4 py-12">
        <LeadCaptureBlock serviceName="Travaux" cityName={city} />
        <div className="mt-6">
          <CallbackForm serviceName="Travaux" cityName={city} />
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-100 bg-white px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-center text-xl font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-6 space-y-3">
            {faqs.map((faq) => (
              <details key={faq.question} className="group rounded-xl border border-slate-200">
                <summary className="flex cursor-pointer items-center justify-between px-5 py-4 text-sm font-medium text-slate-900">
                  {faq.question}
                  <span className="ml-2 text-slate-400 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-500">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="border-t border-slate-100 bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-lg font-bold text-slate-900">Travaux dans d'autres villes</h2>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {nearbyCities.map((c) => {
              const slug = c.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <Link key={c} to={`/travaux/${slug}`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary transition-all">
                  Travaux à {c}
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
